import { DropzoneFile } from 'components/Dropzone/utils';
import { BrandingColors, BrandingLogos, ObjectValues } from 'types';
import { User } from './User';
import { Phone } from 'phoneNumberUtils';

export type Enterprise = {
  _id: string;

  isLink?: boolean;
  isRWC?: boolean;
  isDemo?: boolean; // is RWC demo

  brandName: string;

  status: EnterpriseStatus;
  type: EnterpriseType;

  principals: EnterpriseMember[];
  supports: EnterpriseMember[];

  businessUnits: EnterpriseBusinessUnit[];
  memberSeniorities: EnterpriseSeniority[];
  memberLocations: EnterpriseLocation[];

  //virtual properties
  numberOfBusinessUnits: number;
  numberOfEmployees: number;
  numberOfDeals: number;
} & BrandingColors &
  BrandingLogos;

export const ENTERPRISE_STATUS = {
  DRAFTED: 'drafted',
  PUBLISHED: 'published',
} as const;

type EnterpriseStatus = ObjectValues<typeof ENTERPRISE_STATUS>;

export type EnterpriseMember = {
  _id: string;

  existUser?: boolean;
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  profileImage: DropzoneFile;
  invitationStatus: EnterpriseMemberInvitationStatus;
  status: EnterpriseMemberStatus;
  isEnterpriseHead?: boolean;

  // for sorting
  businessUnitName: string;
  seniority: string;
  location: string;

  //for enterprise analytics
  totalDeals: number;
  successFee: number;
  ev: number;
  totalListings: number;
  totalLeads: number;
  totalContacts: number;
  lastSeen: Date;
  lastInviteSent: Date;

  //mobile and linkedin shouldn't be sortable - you can get from user
  userId: string;
  user: Partial<User> | null;

  //enterpriseId is required for all members
  //business unit and entity will have just employees
  enterpriseId: string;
  enterprise: Enterprise | null;

  enterpriseEntityId: string;
  enterpriseEntity: Partial<EnterpriseEntity>;

  enterpriseBusinessUnitId: string;
  enterpriseBusinessUnit: Partial<EnterpriseBusinessUnit> | null;

  enterpriseSeniorityId: string;
  enterpriseSeniority: Partial<EnterpriseSeniority> | null;

  enterpriseLocationId: string;
  enterpriseLocation: Partial<EnterpriseLocation> | null;
};

export const ENTERPRISE_MEMBER_INVITATION_STATUS = {
  NOT_INVITED: 'not_invited',
  INVITATION_SENT: 'invitation_sent',
  ACCEPTED: 'accepted',
  ACTIVE: 'active',
} as const;

export type EnterpriseMemberInvitationStatus = ObjectValues<
  typeof ENTERPRISE_MEMBER_INVITATION_STATUS
>;

export const ENTERPRISE_MEMBER_STATUS = {
  DRAFTED: 'drafted',
  PUBLISHED: 'published',
} as const;

type EnterpriseMemberStatus = ObjectValues<typeof ENTERPRISE_MEMBER_STATUS>;

type EnterpriseEntity = {
  _id: string;

  name: string;
  principalIds: string[];

  enterpriseId: string;
  enterprise: Partial<Enterprise> | null;
};

export type EnterpriseBusinessUnit = {
  _id: string;
  name: string;
  enterpriseId: string;
};

export type EnterpriseSeniority = {
  _id: string;
  name: string;
  enterpriseId: string;
};

export type EnterpriseLocation = {
  _id: string;
  name: string;
  enterpriseId: string;
};

export type UpdateEnterpriseValue = Partial<
  Pick<Enterprise, 'brandName' | 'type' | 'primaryColor' | 'secondaryColor'> & {
    principalEmails?: string[];
    supportEmails?: string[];
  }
>;

export type UpdateEnterpriseMember = Pick<
  EnterpriseMember,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'title'
  | 'enterpriseSeniorityId'
  | 'enterpriseBusinessUnitId'
  | 'enterpriseLocationId'
> & {
  linkedinAccount: string;
  phone: Phone;
  profileImage?: DropzoneFile;
};

export const ENTERPRISE_TYPE = {
  GENERAL: 'general',
  LINK: 'link',
  RAY_WHITE: 'raywhite',
} as const;

export type EnterpriseType = ObjectValues<typeof ENTERPRISE_TYPE>;
